<!--
  v3.5 (May 12) Inventory Adjust dialog.

  Replaces the broken pencil button on the Inventory page (it used to
  only log to the console). Lets the user change qty and/or last cost
  with a mandatory reason code that goes into the movement audit trail.
-->
<template>
  <v-dialog v-model="visible" max-width="520" persistent>
    <v-card v-if="item">
      <v-card-title class="d-flex align-center pa-3">
        <v-icon class="me-2" color="primary">mdi-pencil</v-icon>
        Adjust stock
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" density="comfortable"
               @click="close" />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-3">
        <div class="text-body-2 mb-2">
          <strong>{{ item.part_number || ('Part #' + item.part_id) }}</strong>
          <span v-if="item.part_name"> &mdash; {{ item.part_name }}</span>
        </div>
        <div class="text-caption text-secondary mb-3">
          Warehouse: {{ item.warehouse_name || item.warehouse_code }}
          ({{ item.warehouse_code }})
        </div>

        <v-row dense>
          <v-col cols="6">
            <v-text-field
              v-model.number="form.new_quantity"
              type="number" min="0" step="1"
              :label="`New On Hand (was ${item.quantity_on_hand})`"
              variant="outlined" density="compact" hide-details
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.number="form.new_last_cost"
              type="number" min="0" step="0.01"
              :label="`New Last Cost (was ${item.last_cost || '-'})`"
              prefix="$"
              variant="outlined" density="compact" hide-details
            />
          </v-col>
        </v-row>

        <v-select
          v-model="form.reason_code"
          :items="reasons"
          label="Reason *"
          variant="outlined" density="compact"
          class="mt-3" hide-details
        />

        <v-textarea
          v-model="form.reason_note"
          label="Note (optional)"
          variant="outlined" density="compact"
          rows="2" class="mt-3" hide-details
          placeholder="e.g. counted during weekly audit"
        />

        <div class="text-caption text-secondary mt-3">
          <v-icon size="14" class="me-1">mdi-information-outline</v-icon>
          A movement record will be created in the audit log.
        </div>
      </v-card-text>
      <v-card-actions class="pa-3">
        <span v-if="errorText" class="text-error text-body-2 me-2">
          {{ errorText }}
        </span>
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn variant="elevated" color="primary"
               :loading="saving" :disabled="!canSave" @click="save">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, reactive } from 'vue'
import axios from '@/api/client'
import { useNotify } from '@/composables/useNotify'

const { notify } = useNotify()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  item: { type: Object, default: null },
})
const emit = defineEmits(['update:modelValue', 'saved'])
const visible = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})

const reasons = [
  { title: 'Count correction',  value: 'count_correction' },
  { title: 'Damaged / scrapped', value: 'damage' },
  { title: 'Found',             value: 'found' },
  { title: 'Lost',              value: 'lost' },
  { title: 'Cost only change',  value: 'cost_only' },
  { title: 'Other',             value: 'other' },
]

const form = reactive({
  new_quantity: 0,
  new_last_cost: null,
  reason_code: 'count_correction',
  reason_note: '',
})
const saving = ref(false)
const errorText = ref('')

watch(() => props.modelValue, v => {
  if (v && props.item) {
    form.new_quantity  = props.item.quantity_on_hand ?? 0
    form.new_last_cost = props.item.last_cost ?? null
    form.reason_code   = 'count_correction'
    form.reason_note   = ''
    errorText.value    = ''
  }
})

const canSave = computed(() => {
  if (!props.item) return false
  const qtyChanged = Number(form.new_quantity) !== Number(props.item.quantity_on_hand)
  const costChanged = form.new_last_cost !== null
                      && Number(form.new_last_cost) !== Number(props.item.last_cost || 0)
  return (qtyChanged || costChanged) && !!form.reason_code
})

async function save() {
  if (!canSave.value || !props.item) return
  saving.value = true
  errorText.value = ''
  try {
    const payload = {
      new_quantity: form.new_quantity,
      reason_code: form.reason_code,
      reason_note: form.reason_note,
    }
    if (form.new_last_cost !== null && form.new_last_cost !== '') {
      payload.new_last_cost = form.new_last_cost
    }
    const r = await axios.post(`/parts/inventory/${props.item.id}/adjust/`, payload)
    notify.success(
      `Stock adjusted: ${r.data.part_number || 'part'} - on hand ${r.data.quantity_on_hand}`
    )
    emit('saved', r.data)
    close()
  } catch (e) {
    console.error('[InventoryAdjustDialog] save failed', e)
    errorText.value = e?.response?.data?.error || 'Could not save adjustment'
    notify.error(errorText.value)
  } finally {
    saving.value = false
  }
}

function close() { visible.value = false }
</script>
